import { forwardRef, ReactNode, useCallback, useMemo } from "react";
import { FormatOptionLabelMeta, PropsValue, SelectInstance } from "react-select";
import { RefType } from "../../../../types";
import { genericMemo } from "../../../../utils";
import { Select } from "../Select";
import {
  SelectFilterOptionOption,
  SelectOnChangeFunctionSignature,
  SimpleSelectComponentType,
  SimpleSelectOptionType,
  SimpleSelectProps,
} from "./types";
import {
  convertActionMeta,
  mapFromSimpleSelectOption,
  mapToSimpleSelectOption,
  mapValueFromSimpleSelectOption,
  mapValueToSimpleSelectOption,
} from "./utils";

export const SimpleSelect = genericMemo(forwardRef(<
  T extends string | number,
  IsMulti extends boolean = boolean
>(
  {
    options,
    value,
    defaultValue,
    onChange,
    getOptionLabel,
    getOptionValue,
    formatOptionLabel,
    filterOption,
    ...props
  }: SimpleSelectProps<T, IsMulti>,
  ref: RefType<SelectInstance<SimpleSelectOptionType<T>, IsMulti>>,
) => {
  const wrappedOptions: SimpleSelectOptionType<T>[] = useMemo(
    () => options?.map(x => mapToSimpleSelectOption(x)!) || [],
    [ options ],
  );

  const wrappedValue: PropsValue<SimpleSelectOptionType<T>> | undefined = useMemo(
    () => mapValueToSimpleSelectOption(value),
    [ value ],
  );

  const wrappedDefaultValue: PropsValue<SimpleSelectOptionType<T>> | undefined = useMemo(
    () => mapValueToSimpleSelectOption(defaultValue),
    [ defaultValue ],
  );

  const wrappedOnChange: SelectOnChangeFunctionSignature<SimpleSelectOptionType<T>, IsMulti> = useCallback(
    (newValue, actionMeta) => onChange?.(mapValueFromSimpleSelectOption(newValue), convertActionMeta(actionMeta)),
    [ onChange ],
  );

  const wrappedGetOptionLabel: ((value: SimpleSelectOptionType<T>) => string) | undefined = useMemo(() => {
    if (getOptionLabel) {
      return (value: SimpleSelectOptionType<T>) => getOptionLabel?.(mapFromSimpleSelectOption<T>(value));
    } else {
      return undefined;
    }
  }, [ getOptionLabel ]);

  const wrappedGetOptionValue: ((value: SimpleSelectOptionType<T>) => string) | undefined = useMemo(() => {
    if (getOptionValue) {
      return (value: SimpleSelectOptionType<T>) => getOptionValue(mapFromSimpleSelectOption<T>(value));
    } else {
      return undefined;
    }
  }, [ getOptionValue ]);

  const wrappedFormatOptionLabel: ((data: SimpleSelectOptionType<T>, formatOptionLabelMeta: FormatOptionLabelMeta<SimpleSelectOptionType<T>>) => ReactNode) | undefined = useMemo(() => {
    if (formatOptionLabel) {
      return (data: SimpleSelectOptionType<T>, formatOptionLabelMeta: FormatOptionLabelMeta<SimpleSelectOptionType<T>>) =>
        formatOptionLabel(
          mapFromSimpleSelectOption<T>(data),
          {
            ...formatOptionLabelMeta,
            selectValue: formatOptionLabelMeta.selectValue.map(x => mapFromSimpleSelectOption<T>(x)),
          },
        );
    } else {
      return undefined;
    }
  }, [ formatOptionLabel ]);

  const wrappedFilterOption: ((option: SelectFilterOptionOption<SimpleSelectOptionType<T>>, inputValue: string) => boolean) | undefined = useMemo(() => {
    if (filterOption) {
      return (option: SelectFilterOptionOption<SimpleSelectOptionType<T>>, inputValue: string) =>
        filterOption(
          {
            ...option,
            data: mapFromSimpleSelectOption<T>(option.data)
          },
          inputValue
        );
    } else {
      return undefined;
    }
  }, [ filterOption ]);

  return (
    <Select<SimpleSelectOptionType<T>, IsMulti>
      {...props}
      options={wrappedOptions}
      value={wrappedValue}
      defaultValue={wrappedDefaultValue}
      onChange={wrappedOnChange}
      getOptionLabel={wrappedGetOptionLabel}
      getOptionValue={wrappedGetOptionValue}
      formatOptionLabel={wrappedFormatOptionLabel}
      filterOption={wrappedFilterOption}
      ref={ref}
    />
  );
}) as SimpleSelectComponentType);



